import { useMutation, useQuery } from "@apollo/client";
import { ArrowDown, ArrowUp, Edit, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { LoyaltyTier, LoyaltyTiersQuery } from "@/gql/graphql";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { ErrorAlert } from "@/lib/components/error-alert";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { useSalonStore } from "@/lib/store/salon";
import {
  DELETE_LOYALTY_TIER_MUTATION,
  LOYALTY_TIERS_QUERY,
  REORDER_LOYALTY_TIERS_MUTATION,
} from "../../../../../graphql/loyalty";
import { TierFormDialog } from "../components/tier-form-dialog";

export function TierManagementPage() {
  const navigate = useNavigate();
  const { t } = useTranslation("settings");
  const isMobile = useIsMobile();
  const { currentSalon } = useSalonStore();
  const [editingTier, setEditingTier] = useState<LoyaltyTier | null>(null);

  const { data, loading, error } = useQuery<LoyaltyTiersQuery>(LOYALTY_TIERS_QUERY, {
    variables: { franchiseId: currentSalon?.franchiseId || "" },
    skip: !currentSalon?.franchiseId,
  });

  const [deleteTier] = useMutation(DELETE_LOYALTY_TIER_MUTATION, {
    refetchQueries: [
      { query: LOYALTY_TIERS_QUERY, variables: { franchiseId: currentSalon?.franchiseId } },
    ],
  });

  const [reorderTiers] = useMutation(REORDER_LOYALTY_TIERS_MUTATION, {
    refetchQueries: [
      { query: LOYALTY_TIERS_QUERY, variables: { franchiseId: currentSalon?.franchiseId } },
    ],
  });

  const tiers = data?.loyaltyTiers || [];

  const handleDeleteTier = async (tierId: string) => {
    try {
      await deleteTier({
        variables: { id: tierId },
      });
    } catch {
      // Error is surfaced via the mutation state
    }
  };

  const handleReorderTier = async (tierId: string, direction: "up" | "down") => {
    const currentIndex = tiers.findIndex((t) => t.id === tierId);
    if (currentIndex === -1) return;

    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= tiers.length) return;

    const reorderedTiers = [...tiers];
    const [movedTier] = reorderedTiers.splice(currentIndex, 1);
    reorderedTiers.splice(newIndex, 0, movedTier);

    try {
      await reorderTiers({
        variables: {
          franchiseId: currentSalon?.franchiseId || "",
          tierIds: reorderedTiers.map((t) => t.id),
        },
      });
    } catch {
      // Error is surfaced via the mutation state
    }
  };

  const formatPointRange = (minPoints: number, maxPoints?: number | null) => {
    if (maxPoints === null || maxPoints === undefined) {
      return `${minPoints}+ points`;
    }
    return `${minPoints}-${maxPoints} points`;
  };

  return (
    <div>
      <BaseNavigationBar
        title={t("rewards.tierManagement.title")}
        subtitle={t("rewards.tierManagement.subtitle")}
        leadElement={
          isMobile ? (
            <BackButton key="back-button" onBack={() => navigate("/settings/workers")} />
          ) : undefined
        }
        trailingElements={[
          <Button
            key="create-tier"
            onClick={() => setEditingTier({} as LoyaltyTier)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            {t("rewards.tierManagement.addTier")}
          </Button>,
        ]}
      />

      <PageContent>
        <div className="space-y-6">
          <ErrorAlert error={error} />

          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : !error ? (
            <div className="space-y-4">
              {tiers.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-8">
                    <div className="text-center">
                      <h3 className="text-lg font-semibold mb-2">
                        {t("rewards.tierManagement.noTiersYet")}
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        {t("rewards.tierManagement.noTiersDescription")}
                      </p>
                      <Button onClick={() => setEditingTier({} as LoyaltyTier)} className="gap-2">
                        <Plus className="h-4 w-4" />
                        {t("rewards.tierManagement.createFirstTier")}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                tiers.map((tier, index) => (
                  <Card key={tier.id}>
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: tier.color }}
                          />
                          <div>
                            <CardTitle className="text-base">{tier.name}</CardTitle>
                            <CardDescription>
                              {formatPointRange(tier.minPoints, tier.maxPoints)}
                            </CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReorderTier(tier.id, "up")}
                            disabled={index === 0}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReorderTier(tier.id, "down")}
                            disabled={index === tiers.length - 1}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingTier(tier as LoyaltyTier)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTier(tier.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {tier.description && (
                        <p className="text-sm text-muted-foreground mb-3">{tier.description}</p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {tier.priorityBooking && (
                          <Badge variant="secondary">
                            {t("rewards.tierManagement.priorityBooking")}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          ) : null}
        </div>
      </PageContent>

      <TierFormDialog
        tier={editingTier}
        open={!!editingTier}
        onClose={() => setEditingTier(null)}
      />
    </div>
  );
}
